import { FC, useState, useEffect, useRef } from 'react';
import { AddressSuggestion } from '@/types/index';
import { addressService } from '@services/address.service';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: AddressSuggestion) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  error,
  required,
  disabled,
}) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Vrai seulement quand l'utilisateur tape lui-même (pas un pré-remplissage programmatique)
  const userIsTyping = useRef(false);

  // Debounce search — ne s'active que si l'utilisateur a tapé
  useEffect(() => {
    if (!userIsTyping.current) return;

    if (value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const results = await addressService.searchAddresses(value);
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Address search error:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (suggestion: AddressSuggestion) => {
    userIsTyping.current = false; // L'adresse est confirmée, stopper la recherche
    onChange(suggestion.address);
    onSelect(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Input Field */}
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            userIsTyping.current = true; // L'utilisateur tape → activer la recherche
            onChange(e.target.value);
          }}
          onFocus={() => suggestions.length > 0 && userIsTyping.current && setShowSuggestions(true)}
          disabled={disabled}
          required={required}
          className={`
            w-full px-4 py-2 border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            dark:bg-gray-700 dark:border-gray-600 dark:text-white
            ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
          `}
          placeholder="Tapez une adresse (ex: Rue en Galil)"
        />

        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.egid}-${index}`}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="
                w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700
                border-b border-gray-100 dark:border-gray-700 last:border-b-0
                transition-colors
              "
            >
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {suggestion.label}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                EGID: {suggestion.egid}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {showSuggestions && !isLoading && value.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucune adresse trouvée. Vérifiez l'orthographe.
          </p>
        </div>
      )}
    </div>
  );
};
